import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RATING_LABELS } from "@/lib/forms";

import { HelpCircle } from "lucide-react";

function Extras({ field, tid, error, explanation, onExplain }) {
  return (
    <>
      {field.instructions && <p id={`${tid}-instructions`} className="text-xs text-slate-500 mt-1">{field.instructions}</p>}
      {onExplain && (
        <button type="button" data-testid={`${tid}-explain`} onClick={() => onExplain(field)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#0A2A5E] underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] rounded">
          <HelpCircle className="w-3 h-3" aria-hidden="true" /> Explain this question in plain language
        </button>
      )}
      {explanation && (
        <div data-testid={`${tid}-explanation`} className="mt-1.5 rounded-lg border border-[#FBD38D] bg-[#FFF7E8] p-2.5 text-xs text-slate-700">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#7C3F00] mb-1">{explanation.label}</div>
          <p>{explanation.text}</p>
        </div>
      )}
      {error && <p id={`${tid}-error`} role="alert" data-testid={`${tid}-error`} className="text-xs text-rose-700 mt-1 font-medium">{error}</p>}
    </>
  );
}

export default function FormField({ field, value, onChange, error, explanation, onExplain }) {
  const tid = `field-${field.id}`;
  const describedBy = [field.instructions && `${tid}-instructions`, error && `${tid}-error`].filter(Boolean).join(" ") || undefined;
  const label = (
    <Label htmlFor={tid} className="text-sm font-medium text-slate-800">
      {field.label}{field.required && <span className="text-rose-600 ml-0.5" aria-hidden="true">*</span>}{field.required && <span className="sr-only"> (required)</span>}
      {field.help && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#C7761A] font-bold">{field.help}</span>}
    </Label>
  );
  const extras = <Extras field={field} tid={tid} error={error} explanation={explanation} onExplain={onExplain} />;

  if (field.type === "rating") {
    return (
      <div className="sm:col-span-2 py-2 border-b border-slate-100 last:border-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        {label}
        <div className="flex gap-1" role="radiogroup" aria-label={field.label} aria-describedby={describedBy} data-testid={tid}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`${tid}-${n}`}
              title={RATING_LABELS[n - 1]}
              onClick={() => onChange(field.id, Number(value) === n ? "" : n)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold border transition-colors ${
                Number(value) === n ? "bg-[#0A2A5E] text-white border-[#0A2A5E]" : "bg-white text-slate-700 border-slate-300 hover:border-[#F5A623] hover:bg-[#FFF7E8]"
              }`}
            >{n}</button>
          ))}
        </div>
      </div>
      {extras}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="py-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox id={tid} data-testid={tid} checked={!!value} onCheckedChange={(v) => onChange(field.id, !!v)} aria-describedby={describedBy} aria-invalid={!!error} className="mt-0.5" />
          <span className="text-sm text-slate-800">{field.label}{field.required && <span className="text-rose-600 ml-0.5" aria-hidden="true">*</span>}</span>
        </label>
        {extras}
      </div>
    );
  }

  return (
    <div className={field.type === "textarea" ? "sm:col-span-2" : ""}>
      {label}
      {field.type === "textarea" && (
        <Textarea id={tid} data-testid={tid} value={value || ""} placeholder={field.placeholder} aria-describedby={describedBy} aria-invalid={!!error} onChange={(e) => onChange(field.id, e.target.value)} className={`mt-1 min-h-[90px] ${error ? "border-rose-500" : ""}`} />
      )}
      {(field.type === "text" || field.type === "date") && (
        <Input id={tid} data-testid={tid} type={field.type} value={value || ""} placeholder={field.placeholder} required={field.required} aria-describedby={describedBy} aria-invalid={!!error} onChange={(e) => onChange(field.id, e.target.value)} className={`mt-1 ${error ? "border-rose-500" : ""}`} />
      )}
      {field.type === "select" && (
        <Select value={value || ""} onValueChange={(v) => onChange(field.id, v)}>
          <SelectTrigger id={tid} data-testid={tid} aria-describedby={describedBy} aria-invalid={!!error} className={`mt-1 ${error ? "border-rose-500" : ""}`}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => <SelectItem key={o} value={o} data-testid={`${tid}-opt-${o.replace(/\W+/g, "-").toLowerCase()}`}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {extras}
    </div>
  );
}
